import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { addNotification } from "../../notifications/notificationsSlice";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

// Types for the v2 saved searches API
export interface SavedSearch {
  id: number;
  name: string;
  resource: "attorneys";
  query: Record<string, any>;
  pinned: boolean;
  is_public: boolean;
  employer_id?: number;
  last_results_count: number | null;
  last_ran_at: string | null;
  created_at: string;
  updated_at: string;
  created_by?: {
    id: number;
    name: string;
  };
}

export interface CreateSavedSearchRequest {
  name: string;
  resource: "attorneys";
  query: Record<string, any>;
  pinned?: boolean;
  is_public?: boolean;
}

export interface UpdateSavedSearchRequest {
  name?: string;
  query?: Record<string, any>;
  pinned?: boolean;
  is_public?: boolean;
}

export const savedSearchesApi = createApi({
  reducerPath: "savedSearchesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: ["SavedSearch"],
  endpoints: (builder) => ({
    // List all saved searches
    getSavedSearches: builder.query<SavedSearch[], void>({
      query: () => "/api/v2/saved_searches",
      providesTags: ["SavedSearch"]
    }),

    // Get single saved search
    getSavedSearch: builder.query<SavedSearch, number>({
      query: (id) => `/api/v2/saved_searches/${id}`,
      providesTags: (result, error, id) => [{ type: "SavedSearch", id }]
    }),

    // Create new saved search
    createSavedSearch: builder.mutation<SavedSearch, CreateSavedSearchRequest>({
      query: (data) => ({
        url: "/api/v2/saved_searches",
        method: "POST",
        body: data
      }),
      invalidatesTags: ["SavedSearch"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Saved search created successfully",
              type: "success"
            })
          );
        } catch (error) {
          dispatch(
            addNotification({
              message: "Failed to create saved search",
              type: "danger"
            })
          );
        }
      }
    }),

    // Update saved search
    updateSavedSearch: builder.mutation<
      SavedSearch,
      { id: number; data: UpdateSavedSearchRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/v2/saved_searches/${id}`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SavedSearch", id },
        "SavedSearch"
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Saved search updated successfully",
              type: "success"
            })
          );
        } catch (error) {
          dispatch(
            addNotification({
              message: "Failed to update saved search",
              type: "danger"
            })
          );
        }
      }
    }),

    // Delete saved search
    deleteSavedSearch: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/v2/saved_searches/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, id) => [
        { type: "SavedSearch", id },
        "SavedSearch"
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Saved search deleted successfully",
              type: "success"
            })
          );
        } catch (error) {
          dispatch(
            addNotification({
              message: "Failed to delete saved search",
              type: "danger"
            })
          );
        }
      }
    })
  })
});

export const {
  useGetSavedSearchesQuery,
  useGetSavedSearchQuery,
  useCreateSavedSearchMutation,
  useUpdateSavedSearchMutation,
  useDeleteSavedSearchMutation
} = savedSearchesApi;
